import React, { useState } from "react";
import { Download, Share, X } from "lucide-react";
import { usePWA } from "../lib/pwa";
import { isNative } from "../lib/nativePlatform";

/**
 * Slim install banner shown above the chat composer on mobile.
 * - Android/Chrome: native `beforeinstallprompt` -> direct install
 * - iOS Safari: opens a 2-step "Tap Share -> Add to Home Screen" sheet
 *
 * Iter XX.181 — Never render inside a Capacitor native shell: the
 * player IS already inside the installed native app; "Add Gully to
 * your home screen" is nonsensical and leaks web/PWA UX into the
 * native experience.
 */
export default function InstallBanner({ sessionId }) {
  const { isInstallable, platform, promptInstall, dismiss } = usePWA(sessionId);
  const [showIOS, setShowIOS] = useState(false);

  if (isNative()) return null;
  if (!isInstallable) return null;

  async function handleInstall() {
    const res = await promptInstall();
    if (res?.showIOSInstructions) setShowIOS(true);
  }

  return (
    <>
      <div
        className="flex items-center gap-2 px-3 py-2 mx-3 my-2 rounded-2xl border border-emerald-900/40 bg-emerald-950/30 text-sm"
        data-testid="install-banner"
      >
        <Download size={16} className="text-emerald-400 flex-none" />
        <button
          onClick={handleInstall}
          className="flex-1 text-left text-neutral-200 hover:text-white"
          data-testid="install-banner-cta"
        >
          {platform === "ios"
            ? "Add Gully to your home screen"
            : "Install Gully"}
          <span className="text-neutral-500 text-xs ml-2 hidden sm:inline">
            opens like an app
          </span>
        </button>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="p-1 text-neutral-500 hover:text-neutral-300"
          data-testid="install-banner-dismiss"
        >
          <X size={14} />
        </button>
      </div>

      {showIOS && (
        <IOSInstallSheet onClose={() => setShowIOS(false)} />
      )}
    </>
  );
}

function IOSInstallSheet({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-40 bg-black/70 flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="ios-install-sheet"
    >
      <div
        className="w-full sm:max-w-md bg-[#111] border border-neutral-800 rounded-t-3xl sm:rounded-3xl p-6"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: "calc(1.5rem + var(--gully-bottom-nav-h, env(safe-area-inset-bottom, 0px)))" }}
      >
        <div className="text-base font-medium mb-1">Add Gully to your Home Screen</div>
        <div className="text-sm text-neutral-400 mb-5">Two taps. Then Gully opens like an app, no browser bar.</div>
        <ol className="space-y-3 text-sm text-neutral-200">
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-emerald-900/50 text-emerald-300 text-xs flex items-center justify-center flex-none">1</span>
            <div className="flex-1">Tap the <Share size={14} className="inline mx-1 -mt-0.5"/> Share button at the bottom of Safari.</div>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-emerald-900/50 text-emerald-300 text-xs flex items-center justify-center flex-none">2</span>
            <div className="flex-1">Scroll down and tap <strong className="text-white">Add to Home Screen</strong>.</div>
          </li>
        </ol>
        <button
          onClick={onClose}
          className="mt-6 w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 font-medium"
          data-testid="ios-install-sheet-ok"
        >
          Got it
        </button>
      </div>
    </div>
  );
}
